/* =========================================================================
   screens-start.tsx — "Start here" (/#/start): a five-minute orientation for
   a first-time TENANT-side user (occupier) considering a long build-to-suit
   lease. CFO-grade, sector-neutral for the lifecycle/finance concepts; the
   loaded Reddam House numbers are called out explicitly as one worked
   example, per spec assumption A3. See src/AGENTS.md change-flow notes.
   ========================================================================= */

import { useEffect } from "react";
import { Section } from "./components";
import { useHashRoute } from "./state";

const START_GUIDE_SEEN_KEY = "cls-start-guide-seen";

function markSeen(): void {
  try {
    localStorage.setItem(START_GUIDE_SEEN_KEY, new Date().toISOString());
  } catch {
    /* private browsing — the acknowledgement still holds for this session */
  }
}

interface LifecycleStage {
  label: string;
  meaning: string;
  youDecide: string;
  screenLabel: string;
  screenHref: string;
}

const STAGES: LifecycleStage[] = [
  {
    label: "Site",
    meaning: "The developer secures land, or you jointly identify one.",
    youDecide: "Location, size and access for your operation.",
    screenLabel: "Assumptions",
    screenHref: "/assumptions",
  },
  {
    label: "Planning & approvals",
    meaning:
      "Government approval to build what's proposed — 6–18 months, the single biggest source of delay risk.",
    youDecide:
      "Whether the proposed use, scale and program match what you need — changes get expensive after this stage.",
    screenLabel: "Risk register",
    screenHref: "/risk",
  },
  {
    label: "Design",
    meaning:
      "Architectural and engineering drawings turn the approval into a buildable plan.",
    youDecide:
      "Fit-out standard and layout — your operating cost for the next 15–30 years.",
    screenLabel: "Buildings",
    screenHref: "/buildings",
  },
  {
    label: "Procurement & builder pricing",
    meaning:
      "The developer tenders the build to contractors and locks a fixed price.",
    youDecide: "Whether you want visibility into competing builder quotes.",
    screenLabel: "Builder Quotes",
    screenHref: "/quotes",
  },
  {
    label: "Construction",
    meaning:
      "The building goes up. Developer cost, debt and interest accrue here — you don't pay directly, but it drives the rent they'll need.",
    youDecide:
      "Progress oversight, and contribution timing if you're co-funding fit-out.",
    screenLabel: "Developer Economics",
    screenHref: "/developer",
  },
  {
    label: "Practical completion",
    meaning: "The building is finished and handed over.",
    youDecide: "Final inspection, defects, readiness to occupy.",
    screenLabel: "Compliance",
    screenHref: "/compliance",
  },
  {
    label: "Lease commencement",
    meaning:
      "Rent starts. Everything upstream becomes the number you now pay, every year, for the length of the lease.",
    youDecide: "Whether the final rent still matches what was modelled.",
    screenLabel: "Rent Economics",
    screenHref: "/rent-economics",
  },
  {
    label: "Operation",
    meaning: "You occupy and pay rent for the term of the lease.",
    youDecide: "Ongoing affordability — whether the deal is still working.",
    screenLabel: "Operator Economics",
    screenHref: "/operator",
  },
];

interface GlossaryTerm {
  term: string;
  definition: string;
}

const GLOSSARY: GlossaryTerm[] = [
  {
    term: "Cost → rent bridge",
    definition:
      "The developer's logic for turning what they spent (land, construction, finance, margin) into what they ask you to pay. TENURE rebuilds this bridge independently so you can see whether the ask matches the cost, or overshoots it.",
  },
  {
    term: "Quote TPC vs gross TDC",
    definition:
      "Quote TPC is the supplier-reported package total; its scope varies with each quote and must be checked against stated inclusions and exclusions. Gross pre-finance TDC is the model's reconciled whole-development cost denominator before construction debt interest and line fees. Funding contributions do not reduce it.",
  },
  {
    term: "Yield on cost",
    definition:
      "Stabilised Year-1 NOI ÷ gross pre-finance total development cost. Plain-English read: for every dollar in the whole development cost base, how many cents a year comes back as landlord NOI. Low means the deal is hard for the developer to justify; high may mean you're overpaying.",
  },
  {
    term: "Cap rate",
    definition:
      "The return an investor would accept to buy the finished, leased building. The developer uses it to estimate resale value — which underwrites how much rent they need from you today.",
  },
  {
    term: "Escalation",
    definition:
      "How your rent grows each year (fixed %, CPI, or periodic market review). Small differences compound hugely over a 15–30 year term — always check which method applies.",
  },
  {
    term: "ZOPA",
    definition:
      "Zone of possible agreement — the band between the lowest rent the developer can accept and the highest you can afford. A negotiable deal has a real ZOPA; a deal with none is either a walk-away or needs the cost basis reopened.",
  },
  {
    term: "Occupancy-cost ratio",
    definition:
      "Your total rent as a share of your own revenue. The headline affordability guardrail — whether this lease is sustainable for your operation, independent of whether it's fair to the developer.",
  },
  {
    term: "Incentives (effective vs face rent)",
    definition:
      "A landlord contribution or rent-free period lowers your real cost below the face rent printed in the lease. Always negotiate and evaluate on effective rent, not face rent.",
  },
];

interface FiveStep {
  step: string;
  detail: string;
  buttonLabel: string;
  href: string;
}

const FIVE_STEPS: FiveStep[] = [
  {
    step: "Replace the assumptions with yours",
    detail:
      "Site cost, construction cost basis, lease term, your fee/revenue structure. Every field has a hint explaining what it drives.",
    buttonLabel: "Go to Assumptions",
    href: "/assumptions",
  },
  {
    step: "Set up your buildings",
    detail:
      "GFA, NLA, enrolment/seat capacity and $/m² construction cost, per building.",
    buttonLabel: "Go to Buildings",
    href: "/buildings",
  },
  {
    step: "Add builder quotes",
    detail:
      "Load competing bids for a like-for-like comparison — normalised for exclusions, not just the headline number.",
    buttonLabel: "Go to Builder Quotes",
    href: "/quotes",
  },
  {
    step: "Check the rent against ZOPA",
    detail:
      "The cost→rent bridge, the ZOPA band, and the gap between what's asked and what the numbers support.",
    buttonLabel: "Go to Rent Economics",
    href: "/rent-economics",
  },
  {
    step: "Export the board pack",
    detail:
      "The same numbers as PDF and Excel, ready for your finance committee.",
    buttonLabel: "Go to Report",
    href: "/report",
  },
];

export function StartGuide() {
  const [, navigate] = useHashRoute();

  // "any navigation away" — covers every deep-link button below, keyboard
  // shortcuts, and the sidebar, not just the explicit dismiss button.
  useEffect(() => {
    return () => {
      markSeen();
    };
  }, []);

  const dismiss = () => {
    markSeen();
    navigate("/");
  };

  return (
    <div className="page fade-in" data-testid="screen-start-guide">
      <div
        className="hero start-guide-hero"
        style={{ padding: "40px 40px 32px", marginBottom: 28 }}
      >
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="row gap-2" style={{ marginBottom: 16 }}>
            <span className="pill brand">Start here</span>
          </div>
          <h2
            className="h2"
            style={{
              margin: "8px 0 0",
              maxWidth: "26ch",
              color: "hsl(var(--c-foreground))",
            }}
          >
            Before you sign a long lease, understand what you're actually buying
          </h2>
          <p className="p-lead" style={{ marginTop: 12, maxWidth: "70ch" }}>
            A build-to-suit lease isn't a rental agreement for an existing
            building — you're funding, through your rent, the construction of a
            building that doesn't exist yet. TENURE models both sides of that
            deal: what it actually costs to build, what return the developer
            needs to agree to build it, and whether the rent they're asking is
            fair, high, or negotiable. Five minutes, skippable any time —
            nothing here is required to use the model.
          </p>
          <div style={{ marginTop: 20 }}>
            <button
              type="button"
              className="btn primary"
              data-testid="start-guide-dismiss"
              onClick={dismiss}
            >
              Got it — take me to the model
            </button>
          </div>
        </div>
      </div>

      <div
        className="card elevated card-pad start-guide-callout"
        data-testid="start-guide-worked-example"
      >
        <span className="eyebrow">Worked example loaded</span>
        <p className="p-small" style={{ marginTop: 8, marginBottom: 0 }}>
          This model ships pre-loaded with one worked example — a K-12 school
          group at North Ryde. The lifecycle and financial mechanics below apply
          to any long-lease occupier: a hospital or aged-care operator, a
          university, a logistics or data-centre tenant. Tuition and enrolment
          fields you'll see elsewhere belong to the loaded example — swap them
          for your own revenue drivers.
        </p>
      </div>

      <Section
        eyebrow="Timeline"
        title="What happens between today and move-in"
        lede="Every build-to-suit lease is a multi-year process before it becomes a number. Each stage links to the TENURE screen that answers it."
      >
        <div className="start-guide-stepper" data-testid="start-guide-stepper">
          {STAGES.map((stage, i) => (
            <div
              className="start-guide-stage card elevated card-pad"
              key={stage.label}
            >
              <div className="start-guide-stage-badge">{i + 1}</div>
              <div className="eyebrow" style={{ marginTop: 10 }}>
                {stage.label}
              </div>
              <p className="p-small" style={{ marginTop: 6 }}>
                {stage.meaning}
              </p>
              <p className="p-micro" style={{ marginTop: 8 }}>
                <strong>You decide:</strong> {stage.youDecide}
              </p>
              <button
                type="button"
                className="btn ghost start-guide-stage-link"
                onClick={() => navigate(stage.screenHref)}
              >
                {stage.screenLabel} →
              </button>
            </div>
          ))}
        </div>
      </Section>

      <Section
        eyebrow="Glossary"
        title="What the numbers mean"
        lede="Eight terms that explain the whole deal — lifted from the tooltips you'll meet on the live screens."
      >
        <div
          className="grid g-2 start-guide-glossary"
          data-testid="start-guide-glossary"
        >
          {GLOSSARY.map((g) => (
            <div
              className="card elevated card-pad start-guide-term"
              key={g.term}
            >
              <div className="eyebrow">{g.term}</div>
              <p className="p-small" style={{ marginTop: 6, marginBottom: 0 }}>
                {g.definition}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        eyebrow="Five steps"
        title="Use TENURE, from worked example to your own numbers"
      >
        <div className="start-guide-steps" data-testid="start-guide-steps">
          {FIVE_STEPS.map((s, i) => (
            <div
              className="start-guide-step card elevated card-pad"
              key={s.step}
            >
              <div className="start-guide-step-num">{i + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="eyebrow">{s.step}</div>
                <p
                  className="p-small"
                  style={{ marginTop: 4, marginBottom: 0 }}
                >
                  {s.detail}
                </p>
              </div>
              <button
                type="button"
                className="btn ghost"
                onClick={() => navigate(s.href)}
              >
                {s.buttonLabel} →
              </button>
            </div>
          ))}
        </div>
      </Section>

      <div className="p-micro start-guide-not" style={{ marginTop: 24 }}>
        TENURE is a modelling and diagnostic tool. It is not tax, accounting or
        legal advice, not a substitute for a registered quantity surveyor, and
        nothing here creates a contractual entitlement — verify independently
        before relying on any number in a negotiation or board decision.
      </div>
    </div>
  );
}
